import type { ComponentType, SVGProps } from 'react';
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check.js';
import Network from 'lucide-react/dist/esm/icons/network.js';
import Building2 from 'lucide-react/dist/esm/icons/building-2.js';
import ScrollText from 'lucide-react/dist/esm/icons/scroll-text.js';

import type { GrantRefusalCode } from '../../lib/admin-users.js';

/**
 * Path constants + the admin nav entries, kept OUT of `lib/nav.ts`/`AppShell.tsx`
 * deliberately — this build's brief: "do not touch `App.tsx`/`AppShell.tsx`, export
 * route elements cleanly and report the exact `<Route>`/nav lines". The shape below
 * mirrors `lib/nav.ts`'s own `NavItem`/`visibleNavItems` exactly (same fields, same
 * permission-only gating rule — never `roleCode`) so wiring it in is a copy, not a
 * redesign.
 */
export const ADMIN_ROUTES = {
  users: '/admin/users',
  userNew: '/admin/users/new',
  userDetail: (userId: string) => `/admin/users/${userId}`,
  userSessions: (userId: string) => `/admin/users/${userId}/sessions`,
  orgUnits: '/admin/org-units',
  tenants: '/admin/tenants',
  platformAudit: '/admin/audit',
} as const;

export interface AdminNavItem {
  to: string;
  labelKey: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Every entry is gated — there is no ungated admin nav item. */
  permission: string;
}

/**
 * `user.admin` / `org.admin` are held by Admin/HR (ministry scope) and Super Admin
 * (platform scope) — never by any of the other seven roles (`authorization-matrix.ts`).
 * `tenant.admin` / `audit.read_platform` are held by `super_admin` ONLY, and per R-019
 * a `super_admin` assignment can only ever be scoped at `platform` — so gating on the
 * permission STRING alone (matching `lib/nav.ts`'s own rule) already excludes every
 * non-platform actor without a second, redundant `roleCode`/`scopeLevel` check.
 */
export const ADMIN_NAV_ITEMS: readonly AdminNavItem[] = [
  { to: ADMIN_ROUTES.users, labelKey: 'admin.nav.users', icon: ShieldCheck, permission: 'user.admin' },
  { to: ADMIN_ROUTES.orgUnits, labelKey: 'admin.nav.orgUnits', icon: Network, permission: 'org.admin' },
  { to: ADMIN_ROUTES.tenants, labelKey: 'admin.nav.tenants', icon: Building2, permission: 'tenant.admin' },
  { to: ADMIN_ROUTES.platformAudit, labelKey: 'admin.nav.platformAudit', icon: ScrollText, permission: 'audit.read_platform' },
];

export function visibleAdminNavItems(permissions: readonly string[]): AdminNavItem[] {
  return ADMIN_NAV_ITEMS.filter((item) => permissions.includes(item.permission));
}

/**
 * The five refusal codes `grant-authority.ts` can answer with (API_CONTRACT §13.10
 * amendment 4), mapped to i18n keys. "Forbidden" is not an acceptable rendering of any
 * of these — each gets its own specific, human sentence in `i18n/strings.ts`.
 */
const GRANT_REFUSAL_KEY: Record<GrantRefusalCode, string> = {
  NO_GRANT_AUTHORITY: 'admin.grantRefusal.noGrantAuthority',
  SELF_GRANT: 'admin.grantRefusal.selfGrant',
  ROLE_ABOVE_ACTOR: 'admin.grantRefusal.roleAboveActor',
  SCOPE_ABOVE_ACTOR: 'admin.grantRefusal.scopeAboveActor',
  SCOPE_OUTSIDE_ACTOR: 'admin.grantRefusal.scopeOutsideActor',
  CLEARANCE_ABOVE_ACTOR: 'admin.grantRefusal.clearanceAboveActor',
};

export function grantRefusalKey(code: string): string {
  return GRANT_REFUSAL_KEY[code as GrantRefusalCode] ?? 'admin.grantRefusal.generic';
}
